import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FeedbackModal from '../components/FeedbackModal';
import { BASE_URL } from '../services/api';

const MyFeedback = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const feedbackTypes = {
    bug: { label: 'Bug Report', icon: '🐛', color: '#e53e3e' },
    feature_request: { label: 'Feature Request', icon: '💡', color: '#805ad5' },
    question_suggestion: { label: 'Question Suggestion', icon: '❓', color: '#3182ce' },
    general_message: { label: 'General Message', icon: '💬', color: '#38a169' }
  };

  const statusColors = {
    pending: '#ed8936',
    in_progress: '#4299e1',
    resolved: '#48bb78',
    closed: '#718096'
  };

  useEffect(() => {
    fetchMyFeedback();
  }, []);

  const fetchMyFeedback = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${BASE_URL}/api/feedback/my-feedback`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setFeedbackList(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('Failed to load your feedback');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedFeedback) return;

    setIsSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/feedback/${selectedFeedback._id}/communicate`,
        { message: newMessage },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSelectedFeedback(response.data.feedback);
      setFeedbackList(prev =>
        prev.map(f => f._id === selectedFeedback._id ? response.data.feedback : f)
      );
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleFeedbackSubmitted = () => {
    fetchMyFeedback();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ padding: '30px', maxWidth: '1400px', margin: '0 auto', backgroundColor: '#fff', minHeight: '100vh', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        backgroundColor: '#fff',
        padding: '24px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#667eea',
            marginBottom: '8px'
          }}>
            💬 My Feedback
          </h1>
          <p style={{ fontSize: '14px', color: '#718096', margin: 0 }}>
            Track your submissions and communicate with support
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          style={{
            padding: '12px 24px',
            backgroundColor: '#4299e1',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 8px rgba(66, 153, 225, 0.3)'
          }}
          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#3182ce'}
          onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4299e1'}
        >
          <span style={{ fontSize: '18px' }}>💬</span>
          <span>New Feedback</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fff5f5',
          border: '1px solid #fc8181',
          borderRadius: '8px',
          marginBottom: '20px',
          color: '#c53030',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
          Loading your feedback...
        </div>
      ) : feedbackList.length === 0 ? (
        <div style={{
          backgroundColor: '#fff',
          padding: '60px',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#718096',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
          <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>
            No feedback submitted yet
          </div>
          <div style={{ fontSize: '14px', marginBottom: '20px' }}>
            Submit your first feedback using the button above
          </div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedFeedback ? '400px 1fr' : '1fr',
          gap: '20px'
        }}>
          {/* Feedback List */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            {feedbackList.map(feedback => {
              const typeInfo = feedbackTypes[feedback.type];
              const hasUnreadAdmin = feedback.communications?.some(
                c => c.senderRole === 'admin'
              );
              
              return (
                <div
                  key={feedback._id}
                  onClick={() => setSelectedFeedback(feedback)}
                  style={{
                    backgroundColor: '#fff',
                    padding: '16px',
                    borderRadius: '12px',
                    border: selectedFeedback?._id === feedback._id
                      ? '2px solid #4299e1'
                      : '1px solid #e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: selectedFeedback?._id === feedback._id
                      ? '0 4px 12px rgba(66, 153, 225, 0.2)'
                      : '0 1px 3px rgba(0, 0, 0, 0.05)',
                    position: 'relative'
                  }}
                  onMouseOver={(e) => {
                    if (selectedFeedback?._id !== feedback._id) {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedFeedback?._id !== feedback._id) {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                    }
                  }}
                >
                  {hasUnreadAdmin && (
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#f56565',
                      borderRadius: '50%'
                    }} />
                  )}

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      backgroundColor: `${typeInfo.color}15`,
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: typeInfo.color
                    }}>
                      <span>{typeInfo.icon}</span>
                      <span>{typeInfo.label}</span>
                    </div>

                    <div style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      backgroundColor: `${statusColors[feedback.status]}15`,
                      color: statusColors[feedback.status],
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {feedback.status.replace('_', ' ')}
                    </div>
                  </div>

                  <h3 style={{
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#2d3748',
                    marginBottom: '6px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {feedback.title}
                  </h3>

                  <div style={{
                    fontSize: '13px',
                    color: '#718096',
                    marginBottom: '10px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {feedback.description}
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '12px',
                    color: '#a0aec0'
                  }}>
                    <span>{formatDate(feedback.createdAt)}</span>
                    {feedback.communications && feedback.communications.length > 0 && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#f7fafc',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span>💬</span>
                        <span>{feedback.communications.length}</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feedback Details */}
          {selectedFeedback && (
            <div style={{
              backgroundColor: '#fff',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e2e8f0',
              maxHeight: 'calc(100vh - 200px)',
              overflow: 'auto'
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '20px',
                paddingBottom: '20px',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '12px'
                  }}>
                    <span style={{ fontSize: '28px' }}>
                      {feedbackTypes[selectedFeedback.type].icon}
                    </span>
                    <h2 style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#2d3748',
                      margin: 0
                    }}>
                      {selectedFeedback.title}
                    </h2>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      padding: '4px 12px',
                      backgroundColor: `${feedbackTypes[selectedFeedback.type].color}15`,
                      color: feedbackTypes[selectedFeedback.type].color,
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {feedbackTypes[selectedFeedback.type].label}
                    </span>

                    <span style={{
                      padding: '4px 12px',
                      backgroundColor: `${statusColors[selectedFeedback.status]}15`,
                      color: statusColors[selectedFeedback.status],
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {selectedFeedback.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedFeedback(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: '#718096',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  ×
                </button>
              </div>

              {/* Submitted Date */}
              <div style={{
                marginBottom: '20px',
                padding: '10px 12px',
                backgroundColor: '#f7fafc',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#718096'
              }}>
                Submitted on {formatDate(selectedFeedback.createdAt)}
              </div>

              {/* Description */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2d3748',
                  marginBottom: '8px'
                }}>
                  Description:
                </h4>
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#4a5568',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedFeedback.description}
                </div>
              </div>

              {/* Attachments */}
              {selectedFeedback.attachments && selectedFeedback.attachments.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#2d3748',
                    marginBottom: '8px'
                  }}>
                    Attachments:
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: '12px'
                  }}>
                    {selectedFeedback.attachments.map((attachment, idx) => (
                      <div key={idx} style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#fff'
                      }}>
                        {attachment.type === 'image' ? (
                          <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={attachment.url}
                              alt={attachment.filename}
                              style={{
                                width: '100%',
                                height: '120px',
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }}
                            />
                          </a>
                        ) : attachment.type === 'video' ? (
                          <video
                            controls
                            style={{
                              width: '100%',
                              height: '120px',
                              backgroundColor: '#000'
                            }}
                          >
                            <source src={attachment.url} />
                          </video>
                        ) : (
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '120px',
                              fontSize: '48px',
                              backgroundColor: '#f7fafc',
                              textDecoration: 'none'
                            }}
                          >
                            📄
                          </a>
                        )}
                        <div style={{
                          padding: '6px 8px',
                          fontSize: '11px',
                          color: '#718096',
                          borderTop: '1px solid #e2e8f0',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {attachment.filename}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Notes (if any) */}
              {selectedFeedback.adminNotes && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#2d3748',
                    marginBottom: '8px'
                  }}>
                    Admin Notes:
                  </h4>
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#ebf8ff',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#2c5282',
                    whiteSpace: 'pre-wrap',
                    border: '1px solid #bee3f8'
                  }}>
                    {selectedFeedback.adminNotes}
                  </div>
                </div>
              )}

              {/* Communications */}
              <div>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2d3748',
                  marginBottom: '12px'
                }}>
                  Communication:
                </h4>

                {/* Messages */}
                <div style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  marginBottom: '12px',
                  padding: '12px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '8px'
                }}>
                  {selectedFeedback.communications && selectedFeedback.communications.length > 0 ? (
                    selectedFeedback.communications.map((comm, idx) => (
                      <div
                        key={idx}
                        style={{
                          marginBottom: '12px',
                          padding: '12px',
                          backgroundColor: comm.senderRole === 'admin' ? '#ebf8ff' : '#fff',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '6px'
                        }}>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: '#2d3748'
                          }}>
                            {comm.sender?.name}
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: comm.senderRole === 'admin' ? '#4299e1' : '#718096',
                              color: '#fff',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '600',
                              textTransform: 'uppercase'
                            }}>
                              {comm.senderRole}
                            </span>
                          </span>
                          <span style={{ fontSize: '11px', color: '#a0aec0' }}>
                            {formatDate(comm.createdAt)}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '13px',
                          color: '#4a5568',
                          whiteSpace: 'pre-wrap'
                        }}>
                          {comm.message}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#a0aec0',
                      fontSize: '13px'
                    }}>
                      No messages yet. We'll respond soon!
                    </div>
                  )}
                </div>

                {/* New Message Input */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={2}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '13px',
                      color: '#2d3748',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || isSendingMessage}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '8px',
                      backgroundColor: !newMessage.trim() || isSendingMessage
                        ? '#cbd5e0'
                        : '#4299e1',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: !newMessage.trim() || isSendingMessage
                        ? 'not-allowed'
                        : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isSendingMessage ? 'Sending...' : 'Send'}
                  </button>
                </div>
                <div style={{
                  marginTop: '6px',
                  fontSize: '11px',
                  color: '#a0aec0'
                }}>
                  Press Ctrl+Enter to send
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleFeedbackSubmitted}
      />
    </div>
  );
};

export default MyFeedback;
